import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { useCreateEnglishQuizBlueprintMutation } from "../../slices/englishQuizBlueprintApiSlice";

import { useGetChaptersBySubjectQuery } from "../../slices/chapterApiSlice";

import { useLazyGetEnglishGroupAvailabilityQuery } from "../../slices/englishQuestionApiSlice";

const CreateEnglishQuizBlueprintScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Quiz / Subject
  const quiz = location.state?.quiz || null;

  const quizId =
    location.state?.quizId || quiz?._id || quiz?.id || quiz?.quizId || "";

  const subject =
    location.state?.subjectId ||
    quiz?.questionBankSubject ||
    quiz?.subject ||
    quiz?.questionBank?.subject ||
    "";

  const subjectId =
    typeof subject === "object" ? subject?._id || subject?.id || "" : subject;

  const subjectName =
    typeof subject === "object"
      ? subject?.name || subject?.title || "English"
      : "English";

  // Form
  const [sections, setSections] = useState([]);

  const [shuffleSections, setShuffleSections] = useState(false);

  // API
  const [createBlueprint, { isLoading: creating }] =
    useCreateEnglishQuizBlueprintMutation();

  const [createdBlueprint, setCreatedBlueprint] = useState(null);

  // Group availability
  const [availability, setAvailability] = useState({});

  const [getAvailability] = useLazyGetEnglishGroupAvailabilityQuery();

  // Chapters
  const { data: chaptersResponse = [], isLoading: chaptersLoading } =
    useGetChaptersBySubjectQuery(subjectId, {
      skip: !subjectId,
    });

  const chapters = Array.isArray(chaptersResponse)
    ? chaptersResponse
    : chaptersResponse?.chapters ||
      chaptersResponse?.data ||
      chaptersResponse?.data?.chapters ||
      [];
  const addSection = () => {
    setSections([
      ...sections,
      {
        name: "",
        chapterId: "",
        groupType: "",
        selectionCount: 1,
        randomizeGroups: true,
        randomizeQuestionsWithinGroup: false,
        keepGroupTogether: true,
      },
    ]);
  };
  const updateSection = (index, field, value) => {
    setSections((current) =>
      current.map((section, i) =>
        i === index
          ? {
              ...section,
              [field]: value,
            }
          : section,
      ),
    );
  };
  const removeSection = (index) => {
    setSections((current) => current.filter((_, i) => i !== index));
  };
  const moveSection = (index, direction) => {
    const newSections = [...sections];

    const newIndex = direction === "up" ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newSections.length) {
      return;
    }

    [newSections[index], newSections[newIndex]] = [
      newSections[newIndex],
      newSections[index],
    ];

    setSections(newSections);
  };
  const handleChapterChange = async (index, chapterId) => {
    updateSection(index, "chapterId", chapterId);

    updateSection(index, "groupType", "");

    if (!chapterId) {
      return;
    }

    try {
      const response = await getAvailability({
        chapter: chapterId,
      }).unwrap();

      const data = response?.data || response;

      let groupsByType = data?.groupsByType || {};

      // Convert array response into object
      if (Array.isArray(groupsByType)) {
        groupsByType = groupsByType.reduce((result, item) => {
          result[item.groupType] = Number(item.count || 0);

          return result;
        }, {});
      }

      setAvailability((current) => ({
        ...current,
        [chapterId]: {
          ...data,
          groupsByType,
        },
      }));
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.data?.error ||
          "Failed to load group availability",
      );
    }
  };
  const groupTypes = [
    {
      value: "passage",
      label: "Passage",
    },
    {
      value: "clozeTest",
      label: "Cloze Test",
    },
    {
      value: "commonDirections",
      label: "Common Directions",
    },
    {
      value: "caselet",
      label: "Caselet",
    },
    {
      value: "paraJumble",
      label: "Para Jumble",
    },
    {
      value: "sentenceBased",
      label: "Sentence Based",
    },
    {
      value: "other",
      label: "Other",
    },
  ];

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!quizId) {
      toast.error("Quiz ID is required");
      return;
    }

    if (!sections.length) {
      toast.error("Add at least one blueprint section");
      return;
    }

    // Validate every section individually
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const sectionNumber = i + 1;

      const sectionName = String(section.name ?? "").trim();
      const chapterId = String(section.chapterId ?? "").trim();
      const groupType = String(section.groupType ?? "").trim();
      const selectionCount = Number(section.selectionCount);

      if (!sectionName) {
        toast.error(`Section ${sectionNumber}: Section Name is required`);
        return;
      }

      if (!chapterId) {
        toast.error(`Section ${sectionNumber}: Chapter is required`);
        return;
      }

      if (!groupType) {
        toast.error(`Section ${sectionNumber}: Group Type is required`);
        return;
      }

      if (!Number.isFinite(selectionCount) || selectionCount <= 0) {
        toast.error(
          `Section ${sectionNumber}: Groups to Include must be at least 1`,
        );
        return;
      }
    }

    // Optional: prevent selecting more groups than available
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];

      const chapterData = availability[section.chapterId];

      if (!chapterData) {
        continue;
      }

      const groupsByType = chapterData.groupsByType || {};

      const availableCount = Number(groupsByType[section.groupType] || 0);

      const selectionCount = Number(section.selectionCount);

      if (selectionCount > availableCount) {
        toast.error(
          `Section ${i + 1}: Only ${availableCount} ${section.groupType} groups are available, but ${selectionCount} selected`,
        );
        return;
      }
    }

    try {
      console.log("========== BLUEPRINT SUBMIT ==========");

      console.log("Quiz ID:", quizId);

      console.log("Sections:", JSON.stringify(sections, null, 2));

      console.log("Shuffle Sections:", shuffleSections);

      const response = await createBlueprint({
        quizId,

        blueprint: {
          sections: sections.map((section, index) => ({
            name: String(section.name ?? "").trim(),

            order: index + 1,

            chapter: section.chapterId,

            groupType: section.groupType,

            selectionCount: Number(section.selectionCount),

            keepGroupTogether: Boolean(section.keepGroupTogether),

            randomizeGroups: Boolean(section.randomizeGroups),

            randomizeQuestionsWithinGroup: Boolean(
              section.randomizeQuestionsWithinGroup,
            ),
          })),

          shuffleSections: Boolean(shuffleSections),
        },
      }).unwrap();

      console.log("CREATE BLUEPRINT RESPONSE:", response);

      const blueprint =
        response?.blueprint ||
        response?.data?.blueprint ||
        response?.data ||
        response;

      setCreatedBlueprint(blueprint);

      toast.success("Blueprint created successfully");
    } catch (error) {
      console.error("CREATE BLUEPRINT ERROR:", error);

      toast.error(
        error?.data?.message ||
          error?.data?.error ||
          error?.message ||
          "Failed to create blueprint",
      );
    }
  };

  const resetHandler = () => {
    setSections([]);
    setShuffleSections(false);
    setCreatedBlueprint(null);
    setAvailability({});

    toast.info("Blueprint form reset");
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 px-4 py-8">
      <div className="mx-auto max-w-5xl">
        {/* Back */}

        <Link
          to="/quizzes"
          className="mb-6 inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-blue-600 shadow hover:bg-blue-50"
        >
          ← Back to Quizzes
        </Link>

        {/* Main Card */}

        <div className="rounded-3xl border border-white/30 bg-white/80 p-6 shadow-2xl backdrop-blur-lg md:p-10">
          {/* Header */}

          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-800">
              Create English Quiz Blueprint
            </h1>

            <p className="mt-2 text-gray-500">
              Select complete question groups for each quiz section.
            </p>
          </div>

          {/* Subject */}

          <div className="mb-6 rounded-xl bg-indigo-50 p-4">
            <p className="text-sm font-semibold text-indigo-900">
              Subject: {subjectName}
            </p>

            <p className="mt-1 text-xs text-indigo-600">
              {subjectId || "Subject ID unavailable"}
            </p>
          </div>

          {/* Loading */}

          {chaptersLoading && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Loading chapters...
            </div>
          )}

          {/* Sections Header */}

          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Blueprint Sections
              </h2>

              <p className="text-sm text-gray-500">
                Each section selects complete question groups.
              </p>
            </div>

            <button
              type="button"
              onClick={addSection}
              disabled={creating || chaptersLoading}
              className="rounded-xl bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              + Add Section
            </button>
          </div>

          {/* No Sections */}

          {!sections.length && (
            <div className="mb-6 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-10 text-center">
              <p className="font-semibold text-gray-700">No sections added</p>

              <p className="mt-1 text-sm text-gray-500">
                Click "Add Section" to begin.
              </p>
            </div>
          )}

          {/* Sections */}

          <div className="space-y-6">
            {sections.map((section, index) => {
              const chapterData = availability[section.chapterId];

              const groupsByType = chapterData?.groupsByType || {};

              const availableCount = Number(
                groupsByType[section.groupType] || 0,
              );

              return (
                <div
                  key={index}
                  className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
                >
                  {/* Section Header */}

                  <div className="mb-5 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800">
                      Section {index + 1}
                    </h3>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => moveSection(index, "up")}
                        disabled={index === 0}
                        className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40"
                      >
                        ↑
                      </button>

                      <button
                        type="button"
                        onClick={() => moveSection(index, "down")}
                        disabled={index === sections.length - 1}
                        className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40"
                      >
                        ↓
                      </button>

                      <button
                        type="button"
                        onClick={() => removeSection(index)}
                        className="rounded-lg border border-red-200 px-3 py-1 text-sm text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Section Name */}

                  <div className="mb-5">
                    <label className="mb-2 block font-semibold text-gray-700">
                      Section Name *
                    </label>

                    <input
                      type="text"
                      value={section.name}
                      onChange={(e) =>
                        updateSection(index, "name", e.target.value)
                      }
                      placeholder="Reading Comprehension"
                      className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  {/* Chapter + Group */}

                  <div className="grid gap-5 md:grid-cols-2">
                    {/* Chapter */}

                    <div>
                      <label className="mb-2 block font-semibold text-gray-700">
                        Chapter *
                      </label>

                      <select
                        value={section.chapterId}
                        onChange={(e) =>
                          handleChapterChange(index, e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select Chapter</option>

                        {chapters.map((chapter) => {
                          const id = chapter?._id || chapter?.id;

                          return (
                            <option key={id} value={id}>
                              {chapter?.name ||
                                chapter?.title ||
                                chapter?.chapterName}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Group Type */}

                    <div>
                      <label className="mb-2 block font-semibold text-gray-700">
                        Group Type *
                      </label>

                      <select
                        value={section.groupType}
                        onChange={(e) =>
                          updateSection(index, "groupType", e.target.value)
                        }
                        disabled={!section.chapterId}
                        className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 disabled:opacity-50"
                      >
                        <option value="">Select Group Type</option>

                        {groupTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                            {" ("}
                            {groupsByType[type.value]}
                            {")"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Group Count */}

                  <div className="mt-5">
                    <label className="mb-2 block font-semibold text-gray-700">
                      Groups to Include *
                    </label>

                    <input
                      type="number"
                      min="1"
                      value={section.selectionCount}
                      onChange={(e) =>
                        updateSection(
                          index,
                          "selectionCount",
                          Math.max(1, Number(e.target.value) || 1),
                        )
                      }
                      className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
                    />

                    {section.groupType && chapterData && (
                      <p
                        className={`mt-2 text-sm ${
                          Number(section.selectionCount) > availableCount
                            ? "text-red-600"
                            : "text-gray-500"
                        }`}
                      >
                        {section.selectionCount} of {availableCount} groups
                        available.
                      </p>
                    )}
                  </div>

                  {/* Randomization */}

                  <div className="mt-5 rounded-xl bg-gray-50 p-4">
                    <h4 className="mb-3 font-semibold text-gray-800">
                      Randomization
                    </h4>

                    <div className="space-y-3">
                      <label className="flex gap-3">
                        <input
                          type="checkbox"
                          checked={section.randomizeGroups}
                          onChange={(e) =>
                            updateSection(
                              index,
                              "randomizeGroups",
                              e.target.checked,
                            )
                          }
                        />

                        <span className="text-sm">Randomize Groups</span>
                      </label>

                      <label className="flex gap-3">
                        <input
                          type="checkbox"
                          checked={section.randomizeQuestionsWithinGroup}
                          onChange={(e) =>
                            updateSection(
                              index,
                              "randomizeQuestionsWithinGroup",
                              e.target.checked,
                            )
                          }
                        />

                        <span className="text-sm">Randomize Questions</span>
                      </label>

                      <label className="flex gap-3">
                        <input
                          type="checkbox"
                          checked={section.keepGroupTogether}
                          onChange={(e) =>
                            updateSection(
                              index,
                              "keepGroupTogether",
                              e.target.checked,
                            )
                          }
                        />

                        <span className="text-sm">Keep Group Together</span>
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Shuffle Sections */}

          {sections.length > 0 && (
            <div className="mt-6 rounded-xl bg-gray-50 p-4">
              <label className="flex gap-3">
                <input
                  type="checkbox"
                  checked={shuffleSections}
                  onChange={(e) => setShuffleSections(e.target.checked)}
                />

                <div>
                  <p className="font-semibold text-gray-800">
                    Shuffle Sections
                  </p>

                  <p className="text-sm text-gray-500">
                    Randomize section order when generating the quiz.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Total */}

          {sections.length > 0 && (
            <div className="mt-6 rounded-xl bg-indigo-50 p-4">
              <p className="font-semibold text-indigo-900">
                {sections.reduce(
                  (total, section) =>
                    total + Number(section.selectionCount || 0),
                  0,
                )}{" "}
                complete groups selected.
              </p>

              <p className="mt-1 text-sm text-indigo-600">
                Question count depends on the actual contents of those groups.
              </p>
            </div>
          )}

          {/* Buttons */}

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <button
              type="button"
              onClick={submitHandler}
              disabled={creating}
              className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create Blueprint"}
            </button>

            <button
              type="button"
              onClick={resetHandler}
              className="rounded-xl border border-gray-300 bg-white py-3 font-semibold text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
          </div>

          {/* Success */}

          {createdBlueprint && (
            <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="font-semibold text-green-800">
                Blueprint created successfully
              </p>

              <p className="mt-1 text-sm text-green-700">
                {createdBlueprint?._id ||
                  createdBlueprint?.id ||
                  createdBlueprint?.blueprintId ||
                  "Blueprint saved successfully"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateEnglishQuizBlueprintScreen;
